import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme, themeStyles } from '../context/ThemeContext';
import ParticleBackground from '../components/ParticleBackground';
import { MoonIcon, SunIcon, ChevronDown, ChevronUp } from 'lucide-react';

const Privacy: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const currentTheme = themeStyles[theme];
  
  const sections = [
    { id: 'section1', title: 'حماية الخصوصية' },
    { id: 'section2', title: 'مسؤولية المستخدم' },
    { id: 'section3', title: 'استخدام البيانات' },
    { id: 'section4', title: 'ملفات تعريف الارتباط' },
    { id: 'section5', title: 'نوع البيانات التي نجمعها' },
    { id: 'section6', title: 'مشاركة البيانات' },
    { id: 'section7', title: 'الدفع الإلكتروني' },
    { id: 'section8', title: 'تحديث سياسة الخصوصية' },
    { id: 'section10', title: 'حقوق المستخدم' },
  ];

  // State to track which accordion sections are open (for mobile view)
  const [openSections, setOpenSections] = useState<{[key: string]: boolean}>({
    section1: true
  });

  const toggleSection = (sectionId: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={`flex flex-col min-h-screen ${currentTheme.background}`}>
      {/* Background particles with reduced opacity */}
      <ParticleBackground />

      {/* ---------- HEADER ---------- */}
      <header className={`fixed top-0 w-full z-50 ${theme === 'dark' ? 'bg-[#1a1a2e]/90' : 'bg-[#F7F9FB]/90'} backdrop-blur-lg shadow-md py-3`}>
        <nav className="max-w-7xl mx-auto px-4 py-1 flex items-center justify-between">
          {/* Left side: brand name */}
          <Link to="/" className={`${currentTheme.text} font-bold text-3xl hover:text-[#4B7BFF] transition-colors`}>
            مسبار
          </Link>

          {/* Right side: theme toggle */}
          <div className="flex items-center">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full ${theme === 'dark' ? 'bg-[#2a2a4a] hover:bg-[#3a3a5a] text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'} transition-colors`}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
            </button>
          </div>
        </nav>
      </header>

      {/* ---------- MAIN CONTENT ---------- */}
      <main className="flex-grow pt-24 pb-16 px-4 dir-rtl">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className={`text-4xl md:text-5xl font-bold ${currentTheme.text} mb-3`}>
              سياسة الخصوصية
            </h1>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} text-lg`}>
              آخر تحديث: ١٩ أبريل ٢٠٢٥
            </p>
          </motion.div>
          
          {/* Desktop View */}
          <div className="hidden md:flex flex-row gap-8">
            {/* Right sidebar */}
            <div className="w-1/4">
              <div className={`sticky top-28 ${theme === 'dark' ? 'bg-[#2a2a4a]/50' : 'bg-white'} p-5 rounded-lg shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                <h3 className={`text-xl font-bold ${currentTheme.text} mb-5 text-right border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} pb-3`}>
                  المحتويات
                </h3>
                <ul className="space-y-2 text-right">
                  {sections.map((section, index) => (
                    <li key={section.id} className="mb-1">
                      <button
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full py-2 px-3 text-right rounded-md text-md hover:bg-[#4B7BFF]/10 transition-colors ${currentTheme.text} flex items-center justify-end`}
                      >
                        <span>{section.title}</span>
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} ml-2`}>
                          {index + 1}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Main content */}
            <div className="w-3/4">
              <div className={`${theme === 'dark' ? 'bg-[#2a2a4a]/50' : 'bg-white'} p-6 rounded-lg shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                <section id="section1" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>حماية الخصوصية</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>١</span>
                  </h2>
                  <p className={`${currentTheme.text} text-lg leading-relaxed text-right`}>
                    نلتزم في منصة مسبار بالحفاظ على خصوصية جميع المستخدمين، ولا تتم مشاركة أي بيانات شخصية مع أي طرف خارجي.
                  </p>
                </section>

                <section id="section2" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>مسؤولية المستخدم</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٢</span>
                  </h2>
                  <p className={`${currentTheme.text} text-lg leading-relaxed text-right`}>
                    يقع على المستخدم مسؤولية الحفاظ على اسم المستخدم وكلمة المرور، ويُمنع مشاركتهما مع أي شخص آخر لأي سبب كان.
                  </p>
                </section>

                <section id="section3" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>استخدام البيانات</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٣</span>
                  </h2>
                  <div className="text-right">
                    <p className={`${currentTheme.text} text-lg leading-relaxed`}>
                      يحق للموقع استخدام بعض بيانات المستخدمين بهدف:
                    </p>
                    <ul className={`${currentTheme.text} text-lg leading-relaxed mt-3 space-y-2 pr-0`}>
                      <li className="flex items-center gap-2 justify-end">
                        <span>تحسين تجربة اللعب.</span>
                        <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                      </li>
                      <li className="flex items-center gap-2 justify-end">
                        <span>تطوير المنصة.</span>
                        <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                      </li>
                      <li className="flex items-center gap-2 justify-end">
                        <span>إرسال عروض أو إشعارات تسويقية تتعلق بالموقع.</span>
                        <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                      </li>
                    </ul>
                  </div>
                </section>

                <section id="section4" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>ملفات تعريف الارتباط</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٤</span>
                  </h2>
                  <div className="text-right">
                    <p className={`${currentTheme.text} text-lg leading-relaxed`}>
                      يستخدم الموقع ملفات تعريف الارتباط من أجل:
                    </p>
                    <ul className={`${currentTheme.text} text-lg leading-relaxed mt-3 space-y-2 pr-0`}>
                      <li className="flex items-center gap-2 justify-end">
                        <span>حفظ تفضيلات المستخدم.</span>
                        <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                      </li>
                      <li className="flex items-center gap-2 justify-end">
                        <span>تسريع الأداء.</span>
                        <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                      </li>
                      <li className="flex items-center gap-2 justify-end">
                        <span>توفير تجربة مخصصة لكل مستخدم.</span>
                        <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                      </li>
                    </ul>
                  </div>
                </section>

                <section id="section5" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>نوع البيانات التي نجمعها</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٥</span>
                  </h2>
                  <div className="text-right">
                    <p className={`${currentTheme.text} text-lg leading-relaxed`}>
                      تشمل البيانات التي يخزنها الموقع:
                    </p>
                    <ul className={`${currentTheme.text} text-lg leading-relaxed mt-3 space-y-2 pr-0`}>
                      <li className="flex items-center gap-2 justify-end">
                        <span>الاسم الكامل.</span>
                        <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                      </li>
                      <li className="flex items-center gap-2 justify-end">
                        <span>البريد الإلكتروني.</span>
                        <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                      </li>
                      <li className="flex items-center gap-2 justify-end">
                        <span>رقم الجوال.</span>
                        <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                      </li>
                      <li className="flex items-center gap-2 justify-end">
                        <span>بيانات التفاعل داخل اللعبة (مثل النقاط وسجلات اللعب).</span>
                        <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                      </li>
                    </ul>
                  </div>
                </section>

                <section id="section6" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>مشاركة البيانات</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٦</span>
                  </h2>
                  <p className={`${currentTheme.text} text-lg leading-relaxed text-right`}>
                    لا تتم مشاركة البيانات الشخصية مثل الاسم أو رقم الهاتف أو العمر مع أي جهة خارجية. ويُستخدم كل ما يُجمع فقط داخل المنصة أو لتحسين تجربة المستخدم.
                  </p>
                </section>

                <section id="section7" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>الدفع الإلكتروني</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٧</span>
                  </h2>
                  <div className="text-right">
                    <p className={`${currentTheme.text} text-lg leading-relaxed`}>
                      تتم جميع عمليات الدفع من خلال بوابة دفع آمنة.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      لا يحتفظ الموقع ببيانات البطاقة البنكية.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      أي محاولة لاستخدام بطاقة دفع لا تخص المستخدم تُحمّله المسؤولية القانونية الكاملة.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      جميع المدفوعات للخدمات والاشتراكات غير قابلة للاسترداد أو الاسترجاع تحت أي ظرف من الظروف. بمجرد إتمام عملية الدفع، لا يمكن المطالبة باسترداد المبلغ المدفوع.
                    </p>
                  </div>
                </section>

                <section id="section8" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>تحديث سياسة الخصوصية</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٨</span>
                  </h2>
                  <div className="text-right">
                    <p className={`${currentTheme.text} text-lg leading-relaxed`}>
                      قد نقوم بتحديث سياسة الخصوصية عند الحاجة.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      استخدامك المستمر للموقع يُعتبر موافقة على أي تحديث، لذا ننصح بمراجعة السياسة من وقت لآخر.
                    </p>
                  </div>
                </section>

                <section id="section10" className="mb-4">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>حقوق المستخدم</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٩</span>
                  </h2>
                  <div className="text-right">
                    <p className={`${currentTheme.text} text-lg leading-relaxed`}>
                      يحق لك كمستخدم الوصول إلى بياناتك الشخصية المخزنة لدينا وطلب تصحيحها أو تحديثها.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      كما يمكنك طلب حذف حسابك وبياناتك، مع مراعاة الاستثناءات القانونية والتعاقدية.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      يمكنك التواصل معنا عبر صفحة "تواصل معنا" لممارسة هذه الحقوق أو للاستفسار عن كيفية استخدام بياناتك.
                    </p>
                  </div>
                </section>
              </div>
            </div>
          </div>

          {/* Mobile View - Accordion Style */}
          <div className="md:hidden mt-6">
            <div className={`${theme === 'dark' ? 'bg-[#2a2a4a]/50' : 'bg-white'} rounded-lg shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
              {sections.map((section, index) => (
                <div key={section.id} className={`border-b ${theme === 'dark' ? 'border-gray-700/50' : 'border-gray-200'} last:border-0`}>
                  <button 
                    className={`w-full py-4 px-5 flex items-center justify-between ${currentTheme.text}`}
                    onClick={() => toggleSection(section.id)}
                    aria-expanded={openSections[section.id]}
                  >
                    <span className={`${theme === 'dark' ? 'text-indigo-300' : 'text-indigo-600'}`}>
                      {openSections[section.id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </span>
                    <div className="flex items-center">
                      <span className="font-semibold text-right">{section.title}</span>
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} mr-2`}>
                        {index + 1}
                      </span>
                    </div>
                  </button>
                  
                  {openSections[section.id] && (
                    <div className={`px-5 pb-5 pt-2 text-right ${currentTheme.text}`}>
                      {section.id === 'section1' && (
                        <p className="text-lg leading-relaxed">
                          نلتزم في منصة مسبار بالحفاظ على خصوصية جميع المستخدمين، ولا تتم مشاركة أي بيانات شخصية مع أي طرف خارجي.
                        </p>
                      )}
                      
                      {section.id === 'section2' && (
                        <p className="text-lg leading-relaxed">
                          يقع على المستخدم مسؤولية الحفاظ على اسم المستخدم وكلمة المرور، ويُمنع مشاركتهما مع أي شخص آخر لأي سبب كان.
                        </p>
                      )}
                      
                      {section.id === 'section3' && (
                        <div>
                          <p className="text-lg leading-relaxed">
                            يحق للموقع استخدام بعض بيانات المستخدمين بهدف:
                          </p>
                          <ul className="text-lg leading-relaxed mt-3 space-y-2">
                            <li className="flex items-center gap-2 justify-end">
                              <span>تحسين تجربة اللعب.</span>
                              <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                            </li>
                            <li className="flex items-center gap-2 justify-end">
                              <span>تطوير المنصة.</span>
                              <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                            </li>
                            <li className="flex items-center gap-2 justify-end">
                              <span>إرسال عروض أو إشعارات تسويقية تتعلق بالموقع.</span>
                              <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                            </li>
                          </ul>
                        </div>
                      )}
                      
                      {section.id === 'section4' && (
                        <div>
                          <p className="text-lg leading-relaxed">
                            يستخدم الموقع ملفات تعريف الارتباط من أجل:
                          </p>
                          <ul className="text-lg leading-relaxed mt-3 space-y-2">
                            <li className="flex items-center gap-2 justify-end">
                              <span>حفظ تفضيلات المستخدم.</span>
                              <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                            </li>
                            <li className="flex items-center gap-2 justify-end">
                              <span>تسريع الأداء.</span>
                              <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                            </li>
                            <li className="flex items-center gap-2 justify-end">
                              <span>توفير تجربة مخصصة لكل مستخدم.</span>
                              <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                            </li>
                          </ul>
                        </div>
                      )}
                      
                      {section.id === 'section5' && (
                        <div>
                          <p className="text-lg leading-relaxed">
                            تشمل البيانات التي يخزنها الموقع:
                          </p>
                          <ul className="text-lg leading-relaxed mt-3 space-y-2">
                            <li className="flex items-center gap-2 justify-end">
                              <span>الاسم الكامل.</span>
                              <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                            </li>
                            <li className="flex items-center gap-2 justify-end">
                              <span>البريد الإلكتروني.</span>
                              <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                            </li>
                            <li className="flex items-center gap-2 justify-end">
                              <span>رقم الجوال.</span>
                              <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                            </li>
                            <li className="flex items-center gap-2 justify-end">
                              <span>بيانات التفاعل داخل اللعبة (مثل النقاط وسجلات اللعب).</span>
                              <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                            </li>
                          </ul>
                        </div>
                      )}
                      
                      {section.id === 'section6' && (
                        <p className="text-lg leading-relaxed">
                          لا تتم مشاركة البيانات الشخصية مثل الاسم أو رقم الهاتف أو العمر مع أي جهة خارجية. ويُستخدم كل ما يُجمع فقط داخل المنصة أو لتحسين تجربة المستخدم.
                        </p>
                      )}
                      
                      {section.id === 'section7' && (
                        <div>
                          <p className="text-lg leading-relaxed">
                            تتم جميع عمليات الدفع من خلال بوابة دفع آمنة.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            لا يحتفظ الموقع ببيانات البطاقة البنكية.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            أي محاولة لاستخدام بطاقة دفع لا تخص المستخدم تُحمّله المسؤولية القانونية الكاملة.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            جميع المدفوعات للخدمات والاشتراكات غير قابلة للاسترداد أو الاسترجاع تحت أي ظرف من الظروف. بمجرد إتمام عملية الدفع، لا يمكن المطالبة باسترداد المبلغ المدفوع.
                          </p>
                        </div>
                      )}
                      
                      {section.id === 'section8' && (
                        <div>
                          <p className="text-lg leading-relaxed">
                            قد نقوم بتحديث سياسة الخصوصية عند الحاجة.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            استخدامك المستمر للموقع يُعتبر موافقة على أي تحديث، لذا ننصح بمراجعة السياسة من وقت لآخر.
                          </p>
                        </div>
                      )}

                      {section.id === 'section10' && (
                        <div>
                          <p className="text-lg leading-relaxed">
                            يحق لك كمستخدم الوصول إلى بياناتك الشخصية المخزنة لدينا وطلب تصحيحها أو تحديثها.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            كما يمكنك طلب حذف حسابك وبياناتك، مع مراعاة الاستثناءات القانونية والتعاقدية.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            يمكنك التواصل معنا عبر صفحة "تواصل معنا" لممارسة هذه الحقوق أو للاستفسار عن كيفية استخدام بياناتك.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* ---------- FOOTER ---------- */}
      <footer className={`mt-auto py-6 ${theme === 'dark' ? 'bg-[#1a1a2e]' : 'bg-[#E8ECEF]'}`}>
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className={`${currentTheme.text} text-base`}>
            &copy; {new Date().getFullYear()} مسبار. جميع الحقوق محفوظة.
          </p>
          <div className={`mt-2 flex justify-center space-x-4 ${currentTheme.text}`}>
            <Link to="/contact" className="hover:text-[#4B7BFF] transition-colors text-base">
              تواصل معنا
            </Link>
            <Link to="/terms" className="hover:text-[#4B7BFF] transition-colors text-base">
              الشروط والأحكام
            </Link>
            <Link to="/privacy" className="hover:text-[#4B7BFF] transition-colors text-base font-bold">
              سياسة الخصوصية
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy; 